/**
 * Pricing API service (S9.1 + B2.5b') — client over the pricing endpoints.
 *
 * B2.5b' CLOSES S9.1's OWN RECORDED FOLLOW-UP by name. S9.1 shipped read-only
 * because `mocks/handlers/pricing.ts` was GET-only and pricing mutations were
 * store-first local writes. B2.5a' authored the write surface (12 routes, all
 * rules from `@senditship/domain/pricing-admin`), and this file is the client
 * half: every pricing admin save now leaves the browser.
 *
 * Two gaps closed here beyond the writes:
 *
 * 1. `getDataset()` used to assemble SIX collections. `PricingDataset` has
 *    eleven, and the presence of `productPricing` is the RESOLVER'S MASTER
 *    SWITCH for the −Discount stage walk — so with the old partial dataset the
 *    FE resolver silently skipped group and quantity discounts and emitted no
 *    `discountTrace`, while the MSW `/pricing/resolve` endpoint (which uses the
 *    full mock dataset) applied them. The simulator and the server could agree
 *    on a number and still disagree on the price a customer would be charged.
 *    The dataset is now assembled whole.
 *
 * 2. The PRICE_OVERRIDE audit rows are readable (`listAuditRows`), so the
 *    review lens reads what the SERVER recorded rather than what the client
 *    remembers having sent.
 *
 * Error contract: a refused write answers `{ error, message, field }` with the
 * domain code (see `PRICING_ADMIN_ERROR_STATUS`). `toPricingAdminError` turns
 * that back into the SAME `PricingAdminError` the store threw locally, so
 * every existing `instanceof` / `error.code` branch in the UI keeps working
 * whether the refusal came from the guard or from the wire.
 *
 * CU-1b (the pricing third of the CU-1 flip): every method gains a live-module
 * leg (the `pricing` key, client.ts) executing the contract operations
 * against `/graphql` (pricing.operations.ts -> pricing.gql-map.ts).
 * SIGNATURES ARE UNCHANGED — consumers cannot tell which leg served them,
 * which is the flip's whole claim and what the L2 fe-seam shadow leg
 * byte-diffs.
 *
 * `resolvePrice` is the same IMPORTED function on both sides, so the two
 * worlds cannot disagree on arithmetic; only the wire can drift, and the
 * `pricing-resolve-trace` L2 scenario is what makes that falsifiable.
 *
 * Refusals: the graph reports guard failures as errors-as-data
 * `userErrors[].code` at HTTP 200 (kernel convention) while the mock answers
 * an HTTP status. `throwPricingUserErrors` collapses both onto the SAME
 * `PricingAdminError` the store already throws locally — one code vocabulary,
 * two envelopes, one `instanceof` in the UI.
 *
 * THREE NAMED KNOWN-HOLES on the live leg (register-listed, never silent).
 * Holes 1 and 3 are TRANSPORT holes — a READ that keeps the legacy leg while
 * `pricing` is live — and their reads are carried as DATA in
 * `PRICING_LIVE_LEG_REST_HOLES` below, so `pricingDatasetCoverage.test.ts`
 * COUNTS them instead of restating them. Hole 2 is an echo shape rather than
 * a route, and `patchDefaults` is the write half of hole 3: neither runs on a
 * dataset read, so neither is observable by that guard and both stay prose.
 *
 * 1. `listAuditRows` has NO graph root. The PRICE_OVERRIDE review rows are
 *    computed per write and never persisted, so there is nothing to read
 *    back; the live leg keeps the legacy call, which the MSW seam still
 *    answers in the flipped posture. Closing it = persist the row (a B2.5c'
 *    slice), not transport wiring.
 * 2. A live write echoes `audit: null`. The graph's `PriceOverrideAudit`
 *    carries the REVIEWABLE half (entityType / entityId / reason / summary)
 *    but not the row IDENTITY the legacy row declares (`id`, `action`,
 *    `actorName`, `createdAt`). Minting those in the browser would fabricate
 *    server facts on the one surface whose entire purpose is to record what
 *    the server accepted — so the leg returns null, which `roundTrip`
 *    (pricingStore) already handles by keeping its own optimistic row.
 * 3. The develop-side reads merged in here — `listProductBandGroups`,
 *    `listProductAnchors`, `getDefaults` and the one write `patchDefaults` —
 *    have NO graph root either (`pricing.operations.ts` carries none), so they
 *    keep the legacy leg in both postures. Same rule as hole 1: a live leg
 *    answering an empty band-group set or a null anchor default would assert
 *    "the company configured nothing", which is a different claim from "not
 *    served". Closing it = authoring the roots, not transport wiring.
 *
 *    MEASURED AT THE UNION (2026-09-16), because a register entry is only
 *    legitimate while its evidence holds: the frozen SDL's `Query` type
 *    declares no root under either spelling of these three reads, and
 *    `server/src/modules/pricing/graph.ts` declares thirteen
 *    `builder.queryField(...)` roots — `resolvePrice`, `priceLevels`,
 *    `priceGroupBands`, `markupRules`, `specialItemPrices`,
 *    `customerPriceAssignments`, `costRecords`, `quantityPricingSchedules`,
 *    `pricingGroups`, `customerGroupDiscounts`, `currencyPriceRows`,
 *    `productPricingFields`, `exchangeRates` — none of them these. The five
 *    collections develop added to the DATASET (quantity schedules, pricing
 *    groups, group discounts, currency price rows, product pricing) DO have
 *    roots and ride the graph; these three do not, which is why the count is
 *    eleven legs and three registered legacy reads rather than fourteen legs.
 *    `pricingDatasetCoverage.test.ts` re-derives that absence from the same
 *    frozen SDL on every run, with a served root as its positive control, so
 *    the evidence is checked rather than remembered.
 *
 * MERGED FROM develop (v2026.9.1): the P1 band scope (`productBandGroups`),
 * the D17 per-product anchor picks (`productAnchors`), and the company
 * defaults row (`pricingAnchorDefault` / `costingMethodDefault` /
 * `minimumMarginPct`). `getDataset()` assembles all of them BESIDE the eleven
 * collections above, for exactly the reason the develop side recorded: an
 * engine input the display seat does not assemble is an input that silently
 * does not exist at runtime — the unit suites cannot see it, because they run
 * on `mockPricingDataset` where every member is present.
 */

import {
  PricingAdminError,
  type PriceOverrideAuditRow,
  type PricingAdminErrorCode,
} from '@senditship/domain/pricing-admin';
import apiClient from './axios.config';
import { isLiveModule } from './client';
import { gqlMutate, gqlRequest } from './graphql.client';
import {
  pricingOperations,
  type CostRecordsResult,
  type CurrencyPriceRowsResult,
  type CustomerAssignmentsResult,
  type CustomerGroupDiscountsResult,
  type ExchangeRatesResult,
  type GqlPricingAdminPayload,
  type MarkupRulesResult,
  type PriceGroupBandsResult,
  type PriceLevelsResult,
  type PricingAdminResult,
  type PricingGroupsResult,
  type PricingResolveResult,
  type ProductPricingFieldsResult,
  type QuantitySchedulesResult,
  type SpecialItemPricesResult,
} from './pricing.operations';
import {
  ALLOCATION_BASIS_TO_GQL,
  COST_BASIS_TO_GQL,
  COST_ESTIMATE_TYPE_TO_GQL,
  GROUP_DISCOUNT_KIND_TO_GQL,
  PRICING_ANCHOR_TO_GQL,
  QUANTITY_BASIS_TO_GQL,
  SPECIAL_KIND_TO_GQL,
  mapCostRecord,
  mapCurrencyPriceRow,
  mapCustomerPricing,
  mapExchangeRate,
  mapGroupDiscount,
  mapMarkupRule,
  mapPriceGroupBand,
  mapPriceLevel,
  mapPriceResolution,
  mapPricingGroup,
  mapProductPricingFields,
  mapQuantitySchedule,
  mapSpecialItemPrice,
} from './pricing.gql-map';
import type {
  CostingMethodDefault,
  CostRecord,
  CurrencyPriceRow,
  CustomerGroupDiscount,
  CustomerPricing,
  ExchangeRate,
  MarkupRule,
  PriceGroupBand,
  PriceLevel,
  PriceResolution,
  PriceSource,
  PricingAnchor,
  PricingDataset,
  PricingGroup,
  ProductBandGroup,
  ProductPricingAnchor,
  ProductPricingFields,
  QuantityPricingSchedule,
  SpecialItemPrice,
} from '@/types/api/pricing';

export interface ResolvePriceQuery {
  productId: string;
  customerId?: number | null;
  source?: PriceSource;
  /** ISO date (YYYY-MM-DD); omitted = handler resolves as of today. */
  date?: string;
  qty?: number;
  /** Discribe-19 currency mode: locked rows only, never an FX conversion. */
  currency?: string;
}

/** What a write route answers on success. */
export interface PricingWriteResult<T> {
  data: T;
  audit: PriceOverrideAuditRow | null;
}

interface WriteBody<T> {
  data: T;
  reason?: string;
  actorName?: string;
}

/**
 * Rebuild the domain error from the wire so the UI cannot tell a local
 * refusal from a remote one — the point of sharing one code vocabulary.
 */
export function toPricingAdminError(error: unknown): PricingAdminError | null {
  const body = (error as { response?: { data?: { error?: string; field?: string | null } } })
    ?.response?.data;
  if (!body?.error) return null;
  return new PricingAdminError(body.error as PricingAdminErrorCode, body.field ?? null);
}

async function write<TBody, TResult>(
  method: 'put' | 'patch',
  path: string,
  body: WriteBody<TBody>,
): Promise<PricingWriteResult<TResult>> {
  try {
    const { data } = await apiClient[method]<PricingWriteResult<TResult>>(path, body);
    return data;
  } catch (error) {
    const guard = toPricingAdminError(error);
    if (guard) throw guard;
    throw error;
  }
}

/**
 * The live-leg twin of `toPricingAdminError`: a graph refusal arrives as
 * errors-as-data, and this turns the FIRST row into the identical
 * `PricingAdminError` — same code, same field — so a guard rejection is
 * indistinguishable to the UI whichever seam produced it.
 */
function throwPricingUserErrors(payload: GqlPricingAdminPayload | undefined): void {
  const first = payload?.userErrors?.[0];
  if (!first) return;
  throw new PricingAdminError(first.code as PricingAdminErrorCode, first.field ?? null);
}

/** Read the single payload off a one-root mutation result. */
function adminPayload(result: PricingAdminResult, root: string): GqlPricingAdminPayload {
  const payload = result[root];
  if (!payload) throw new Error(`pricing.api: mutation ${root} answered no payload`);
  throwPricingUserErrors(payload);
  return payload;
}

/**
 * Live writes echo `audit: null` — see known-hole 2 in the file header. The
 * helper exists so every write site says so in ONE place rather than each
 * one quietly returning a literal.
 */
function liveWriteResult<T>(data: T): PricingWriteResult<T> {
  return { data, audit: null };
}

function pickById<T extends { id: string }>(
  rows: readonly T[] | null | undefined,
  id: string,
  root: string,
): T {
  const row = rows?.find((candidate) => candidate.id === id);
  if (!row) throw new Error(`pricing.api: ${root} did not echo row ${id}`);
  return row;
}

/**
 * One transport hole on the live pricing leg: a read that keeps the LEGACY
 * call while `pricing` is flipped live, because the frozen contract carries
 * no root to serve it.
 */
export interface PricingLiveLegRestHole {
  /** The `pricingApi` method that keeps the legacy leg. */
  readonly method: string;
  /** The legacy route it calls, exactly as the wire sees it. */
  readonly route: string;
  /**
   * The `Query` root that WOULD serve it and does not exist. Mechanically
   * derived from `route`, never chosen: the guard re-derives the candidate
   * spellings from the route itself and refuses a name that is not one of
   * them, so an entry cannot be filed under a name picked to miss.
   */
  readonly absentRoot: string;
  /** Whether `getDataset()` reaches it — and so whether the dataset guard counts it. */
  readonly inDataset: boolean;
  /** Why answering it over the graph today would be a FALSE claim, not a fix. */
  readonly why: string;
}

/**
 * KNOWN-HOLES 1 and 3 of the file header, AS DATA — the register the dataset
 * guard counts against instead of restating.
 *
 * This is a register of ABSENT ROOTS, not an allow-list of tolerated calls.
 * The difference is that every entry is falsifiable and the guard fails in
 * BOTH directions: an unregistered REST read on the live leg reds it (the
 * bypass shape `pricingDatasetCoverage.test.ts` exists for), and a registered
 * read that no longer happens reds it too, so a hole cannot be closed and
 * left registered. The absence is re-derived from the frozen SDL on every
 * run, so an entry stops being legitimate the moment its root is authored.
 *
 * Closing an entry is a slice, not transport wiring: author the root (SDL +
 * resolver + persistence), then delete the entry.
 */
export const PRICING_LIVE_LEG_REST_HOLES: readonly PricingLiveLegRestHole[] = [
  {
    method: 'listAuditRows',
    route: '/pricing/audit',
    absentRoot: 'pricingAudit',
    inDataset: false,
    why: 'The server COMPUTES the PRICE_OVERRIDE row per write and never persists it, so there is nothing to read back. A live leg answering [] would assert "no overrides were made".',
  },
  {
    method: 'listProductBandGroups',
    route: '/pricing/product-band-groups',
    absentRoot: 'productBandGroups',
    inDataset: true,
    why: 'P1 band scope, merged from develop. A live leg answering [] would assert "no product is scoped to a band set" — the engine would then fall through to the unscoped bands and price against the wrong set.',
  },
  {
    method: 'listProductAnchors',
    route: '/pricing/product-anchors',
    absentRoot: 'productAnchors',
    inDataset: true,
    why: 'D17 per-product anchor picks. A live leg answering [] would assert "no product overrides the company anchor", and every markup would silently multiply the default cost figure instead of the one the product was priced against.',
  },
  {
    method: 'getDefaults',
    route: '/pricing/defaults',
    absentRoot: 'pricingDefaults',
    inDataset: true,
    why: 'The company defaults row (anchor / costing method / minimum margin). A live leg answering nulls would assert "the company configured nothing" — and a null minimumMarginPct reads as NO FLOOR, which is the one default whose absence silently approves a below-margin sale.',
  },
] as const;

export const pricingApi = {
  /** The ONE resolve endpoint (shared with SO entry and the W9.2 simulator). */
  resolve: async (query: ResolvePriceQuery): Promise<{ data: PriceResolution }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<PricingResolveResult, Record<string, unknown>>(
        pricingOperations.resolve,
        {
          productId: query.productId,
          ...(query.customerId != null ? { customerId: query.customerId } : {}),
          ...(query.source ? { source: query.source } : {}),
          // The legacy query param is `date`; the graph arg is `asOf`. Same
          // fact, two spellings — renamed HERE so no caller learns a second.
          ...(query.date ? { asOf: query.date } : {}),
          ...(query.qty != null ? { qty: query.qty } : {}),
          ...(query.currency ? { currency: query.currency } : {}),
        }
      );
      return { data: mapPriceResolution(result.resolvePrice) };
    }
    const { data } = await apiClient.get<{ data: PriceResolution }>('/pricing/resolve', {
      params: {
        productId: query.productId,
        ...(query.customerId != null ? { customerId: query.customerId } : {}),
        ...(query.source ? { source: query.source } : {}),
        ...(query.date ? { date: query.date } : {}),
        ...(query.qty != null ? { qty: query.qty } : {}),
        ...(query.currency ? { currency: query.currency } : {}),
      },
    });
    return data;
  },

  // ------------------------------------------------------------------ reads

  listLevels: async (): Promise<{ data: PriceLevel[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<PriceLevelsResult, Record<string, never>>(
        pricingOperations.priceLevels,
        {}
      );
      return { data: result.priceLevels.map(mapPriceLevel) };
    }
    const { data } = await apiClient.get<{ data: PriceLevel[] }>('/pricing/levels');
    return data;
  },

  listBands: async (): Promise<{ data: PriceGroupBand[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<PriceGroupBandsResult, Record<string, never>>(
        pricingOperations.priceGroupBands,
        {}
      );
      return { data: result.priceGroupBands.map(mapPriceGroupBand) };
    }
    const { data } = await apiClient.get<{ data: PriceGroupBand[] }>('/pricing/bands');
    return data;
  },

  /**
   * Per-product PRODUCT-GROUP assignments — which band SET buckets a product
   * (P1 band scope). Assembled into the dataset beside the bands themselves,
   * because a band set is unusable without the assignment that selects it.
   *
   * Legacy leg only — known-hole 3 (file header, and `PRICING_LIVE_LEG_REST_HOLES`
   * for the read half): no graph root exists.
   */
  listProductBandGroups: async (): Promise<{ data: ProductBandGroup[] }> => {
    const { data } = await apiClient.get<{ data: ProductBandGroup[] }>(
      '/pricing/product-band-groups'
    );
    return data;
  },

  /**
   * Per-product PRICING ANCHOR picks (D17 / Option B).
   *
   * ⚠ ADDED BY P3 (2026-08-31). `getDataset()` assembled every other engine
   * input and omitted this one, so in the running app `effectiveAnchorOf`
   * always fell through to the company default and a product's own pick had no
   * effect on any price. The unit suite could not see it: it runs on
   * `mockPricingDataset`, where `productAnchors` is present.
   *
   * Legacy leg only — known-hole 3 (file header, and `PRICING_LIVE_LEG_REST_HOLES`
   * for the read half): no graph root exists.
   */
  listProductAnchors: async (): Promise<{ data: ProductPricingAnchor[] }> => {
    const { data } = await apiClient.get<{ data: ProductPricingAnchor[] }>(
      '/pricing/product-anchors'
    );
    return data;
  },

  listMarkupRules: async (params?: {
    priceGroupId?: string;
    source?: PriceSource;
  }): Promise<{ data: MarkupRule[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<MarkupRulesResult, Record<string, unknown>>(
        pricingOperations.markupRules,
        {
          ...(params?.priceGroupId ? { priceGroupId: params.priceGroupId } : {}),
          ...(params?.source ? { source: params.source } : {}),
        }
      );
      return { data: result.markupRules.map(mapMarkupRule) };
    }
    const { data } = await apiClient.get<{ data: MarkupRule[] }>('/pricing/markup-rules', {
      params,
    });
    return data;
  },

  listSpecialPrices: async (params?: {
    customerId?: number;
    productId?: string;
  }): Promise<{ data: SpecialItemPrice[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<SpecialItemPricesResult, Record<string, unknown>>(
        pricingOperations.specialItemPrices,
        {
          ...(params?.customerId != null ? { customerId: params.customerId } : {}),
          ...(params?.productId ? { productId: params.productId } : {}),
        }
      );
      return { data: result.specialItemPrices.map(mapSpecialItemPrice) };
    }
    const { data } = await apiClient.get<{ data: SpecialItemPrice[] }>('/pricing/special-prices', {
      params,
    });
    return data;
  },

  listCustomerPricing: async (params?: {
    customerId?: number;
  }): Promise<{ data: CustomerPricing[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<CustomerAssignmentsResult, Record<string, unknown>>(
        pricingOperations.customerAssignments,
        { ...(params?.customerId != null ? { customerId: params.customerId } : {}) }
      );
      return { data: result.customerPriceAssignments.map(mapCustomerPricing) };
    }
    const { data } = await apiClient.get<{ data: CustomerPricing[] }>('/pricing/customer-pricing', {
      params,
    });
    return data;
  },

  listCostRecords: async (params?: { productId?: string }): Promise<{ data: CostRecord[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<CostRecordsResult, Record<string, unknown>>(
        pricingOperations.costRecords,
        { ...(params?.productId ? { productId: params.productId } : {}) }
      );
      return { data: result.costRecords.map(mapCostRecord) };
    }
    const { data } = await apiClient.get<{ data: CostRecord[] }>('/pricing/cost-records', {
      params,
    });
    return data;
  },

  listQuantitySchedules: async (): Promise<{ data: QuantityPricingSchedule[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<QuantitySchedulesResult, Record<string, never>>(
        pricingOperations.quantitySchedules,
        {}
      );
      return { data: result.quantityPricingSchedules.map(mapQuantitySchedule) };
    }
    const { data } = await apiClient.get<{ data: QuantityPricingSchedule[] }>(
      '/pricing/quantity-schedules'
    );
    return data;
  },

  listPricingGroups: async (): Promise<{ data: PricingGroup[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<PricingGroupsResult, Record<string, never>>(
        pricingOperations.pricingGroups,
        {}
      );
      return { data: result.pricingGroups.map(mapPricingGroup) };
    }
    const { data } = await apiClient.get<{ data: PricingGroup[] }>('/pricing/groups');
    return data;
  },

  listGroupDiscounts: async (params?: {
    customerId?: number;
    groupId?: string;
  }): Promise<{ data: CustomerGroupDiscount[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<CustomerGroupDiscountsResult, Record<string, unknown>>(
        pricingOperations.customerGroupDiscounts,
        {
          ...(params?.customerId != null ? { customerId: params.customerId } : {}),
          ...(params?.groupId ? { groupId: params.groupId } : {}),
        }
      );
      return { data: result.customerGroupDiscounts.map(mapGroupDiscount) };
    }
    const { data } = await apiClient.get<{ data: CustomerGroupDiscount[] }>(
      '/pricing/group-discounts',
      { params }
    );
    return data;
  },

  listCurrencyPriceRows: async (params?: {
    productId?: string;
    currency?: string;
  }): Promise<{ data: CurrencyPriceRow[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<CurrencyPriceRowsResult, Record<string, unknown>>(
        pricingOperations.currencyPriceRows,
        {
          ...(params?.productId ? { productId: params.productId } : {}),
          ...(params?.currency ? { currency: params.currency } : {}),
        }
      );
      return { data: result.currencyPriceRows.map(mapCurrencyPriceRow) };
    }
    const { data } = await apiClient.get<{ data: CurrencyPriceRow[] }>('/pricing/currency-prices', {
      params,
    });
    return data;
  },

  listProductPricing: async (params?: {
    productId?: string;
  }): Promise<{ data: ProductPricingFields[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<ProductPricingFieldsResult, Record<string, unknown>>(
        pricingOperations.productPricingFields,
        { ...(params?.productId ? { productId: params.productId } : {}) }
      );
      return { data: result.productPricingFields.map(mapProductPricingFields) };
    }
    const { data } = await apiClient.get<{ data: ProductPricingFields[] }>(
      '/pricing/product-pricing',
      { params }
    );
    return data;
  },

  /** Accounting-only FX (Discribe-19 §2) — never part of `PricingDataset`. */
  listExchangeRates: async (): Promise<{ data: ExchangeRate[] }> => {
    if (isLiveModule('pricing')) {
      const result = await gqlRequest<ExchangeRatesResult, Record<string, never>>(
        pricingOperations.exchangeRates,
        {}
      );
      return { data: result.exchangeRates.map(mapExchangeRate) };
    }
    const { data } = await apiClient.get<{ data: ExchangeRate[] }>('/pricing/exchange-rates');
    return data;
  },

  /**
   * PRICE_OVERRIDE review rows, newest first — what the SERVER recorded.
   *
   * KNOWN-HOLE 1 (file header; registered in `PRICING_LIVE_LEG_REST_HOLES`):
   * NO live leg. The graph has no root for this
   * read because the server does not persist the rows — it computes the audit
   * per write and returns it on the payload. In the flipped posture the MSW
   * seam still answers this route; a live leg that returned `[]` would assert
   * "no overrides were made", which is a different claim from "not served".
   */
  listAuditRows: async (): Promise<{ data: PriceOverrideAuditRow[] }> => {
    const { data } = await apiClient.get<{ data: PriceOverrideAuditRow[] }>('/pricing/audit');
    return data;
  },

  /**
   * Assemble the FULL dataset the pure resolver + W9 screens operate on.
   * All eleven collections — see the header on why six was a defect — PLUS
   * the develop-side band scope, anchor picks and company defaults.
   */
  getDataset: async (): Promise<PricingDataset> => {
    const [
      bands,
      productBandGroups,
      productAnchors,
      levels,
      markupRules,
      specialPrices,
      customerPricing,
      costRecords,
      productPricing,
      quantitySchedules,
      pricingGroups,
      groupDiscounts,
      currencyPriceRows,
      defaults,
    ] = await Promise.all([
      pricingApi.listBands(),
      pricingApi.listProductBandGroups(),
      pricingApi.listProductAnchors(),
      pricingApi.listLevels(),
      pricingApi.listMarkupRules(),
      pricingApi.listSpecialPrices(),
      pricingApi.listCustomerPricing(),
      pricingApi.listCostRecords(),
      pricingApi.listProductPricing(),
      pricingApi.listQuantitySchedules(),
      pricingApi.listPricingGroups(),
      pricingApi.listGroupDiscounts(),
      pricingApi.listCurrencyPriceRows(),
      pricingApi.getDefaults(),
    ]);
    const { pricingAnchorDefault, costingMethodDefault, minimumMarginPct } = defaults.data.data;
    return {
      priceGroupBands: bands.data,
      // Ships beside the bands, never behind them: a scoped band set that the
      // display seat cannot select is a band set the screen silently ignores.
      productBandGroups: productBandGroups.data,
      // Same rule, same reason: an engine input the display seat does not
      // assemble is an input that silently does not exist at runtime.
      productAnchors: productAnchors.data,
      priceLevels: levels.data,
      markupRules: markupRules.data,
      specialItemPrices: specialPrices.data,
      customerPricing: customerPricing.data,
      costRecords: costRecords.data,
      productPricing: productPricing.data,
      quantityPricingSchedules: quantitySchedules.data,
      pricingGroups: pricingGroups.data,
      customerGroupDiscounts: groupDiscounts.data,
      currencyPriceRows: currencyPriceRows.data,
      // D17 — absent/null ⇒ the fields are simply omitted (chip hidden; the
      // resolver never reads either of them).
      ...(pricingAnchorDefault ? { pricingAnchorDefault } : {}),
      ...(costingMethodDefault ? { costingMethodDefault } : {}),
      // Minimum margin (2026-09-15): null/absent ⇒ omitted ⇒ no floor.
      ...(minimumMarginPct != null ? { minimumMarginPct } : {}),
    };
  },

  /**
   * D17 company-wide pricing defaults — anchor + costing method. Evolved from
   * `/pricing/anchor-default` 2026-08-28. The "config-only this wave" clause
   * is RETRACTED (Option B, 2026-08-31): the anchor is a determination input;
   * the costing method is not.
   *
   * Legacy leg only — known-hole 3 (file header, and `PRICING_LIVE_LEG_REST_HOLES`
   * for the read half): no graph root exists.
   */
  getDefaults: () =>
    apiClient.get<{
      data: {
        pricingAnchorDefault: PricingAnchor | null;
        costingMethodDefault: CostingMethodDefault | null;
        minimumMarginPct: number | null;
      };
    }>('/pricing/defaults'),

  /**
   * Company minimum margin (2026-09-15) — the ONE pricing write that is NOT
   * on the `write()` helper: it predates the B2.5b' admin envelope and answers
   * the defaults row itself rather than `{ data, audit }`. AWAITED by
   * `patchMinimumMarginPct` (usePricing.ts): the store mirrors the RESPONSE
   * value, and a refused write (400 / 403) rejects. The SO handler derives the
   * low-margin hold from the same row this PATCH lands on.
   *
   * Legacy leg only — known-hole 3 (file header, and `PRICING_LIVE_LEG_REST_HOLES`
   * for the read half): no graph root exists.
   */
  patchDefaults: (patch: { minimumMarginPct: number | null }) =>
    apiClient.patch<{
      data: {
        pricingAnchorDefault: PricingAnchor | null;
        costingMethodDefault: CostingMethodDefault | null;
        minimumMarginPct: number | null;
      };
    }>('/pricing/defaults', patch),

  // ----------------------------------------------------------------- writes

  saveLevel: async (level: PriceLevel): Promise<PricingWriteResult<PriceLevel>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.priceLevelSave,
        {
          input: {
            id: level.id,
            code: level.code,
            nameEn: level.nameEn,
            nameZh: level.nameZh,
            sortOrder: level.sortOrder,
            isActive: level.isActive,
          },
        }
      );
      const payload = adminPayload(result, 'priceLevelSave');
      return liveWriteResult(mapPriceLevel(pickById(payload.priceLevels, level.id, 'priceLevels')));
    }
    return write<PriceLevel, PriceLevel>('put', '/pricing/levels', { data: level });
  },

  saveBand: async (band: PriceGroupBand): Promise<PricingWriteResult<PriceGroupBand>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.priceGroupBandSave,
        {
          input: {
            id: band.id,
            group: band.group,
            minInclusive: String(band.minInclusive),
            maxExclusive: band.maxExclusive === null ? null : String(band.maxExclusive),
            currency: band.currency,
            // THE DEACTIVATION, which this leg used to drop. `isBandActive`
            // gates `resolvePriceGroup`, so a retired cut that came back active
            // on the next read RE-PRICES every product in its band — the D-3
            // shape exactly: HTTP 200, `userErrors: []`, the row unmoved.
            // OMITTED when the key is absent, never defaulted to `true`: absent
            // MEANS active on both sides of this seam, and materialising it
            // would send a fact the caller did not state.
            ...(band.isActive === undefined ? {} : { isActive: band.isActive }),
          },
        }
      );
      const payload = adminPayload(result, 'priceGroupBandSave');
      return liveWriteResult(
        mapPriceGroupBand(pickById(payload.priceGroupBands, band.id, 'priceGroupBands'))
      );
    }
    return write<PriceGroupBand, PriceGroupBand>('put', '/pricing/bands', { data: band });
  },

  /** Bulk matrix edit — price-affecting, so `reason` is mandatory. */
  saveMarkupRules: async (
    edits: Array<{
      priceGroupId: string;
      source: PriceSource;
      priceLevelId: string;
      markupPct: number | null;
    }>,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<MarkupRule[]>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.markupRulesSave,
        { input: { edits, reason } }
      );
      const payload = adminPayload(result, 'markupRulesSave');
      return liveWriteResult((payload.markupRules ?? []).map(mapMarkupRule));
    }
    return write<typeof edits, MarkupRule[]>('put', '/pricing/markup-rules', {
      data: edits,
      reason,
      actorName,
    });
  },

  saveSpecialPrice: async (
    row: SpecialItemPrice,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<SpecialItemPrice>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.specialItemPriceSave,
        {
          input: {
            id: row.id,
            customerId: row.customerId,
            productId: row.productId,
            kind: SPECIAL_KIND_TO_GQL[row.kind],
            ...(row.kind === 'fixed'
              ? { fixedPrice: String(row.fixedPrice), currency: row.currency }
              : {
                  markupPct: row.markupPct,
                  // THE REGIME THE MARKUP WAS AUTHORED AGAINST — omitted here
                  // until 2026-09-16, and the omission was not cosmetic.
                  // `SpecialItemPriceInput` declares the field and the resolver
                  // refuses a MARKUP_PCT save without it (VALIDATION_FAILED on
                  // `input.basisAtAuthoring`; `server/src/modules/pricing/
                  // writes.ts` stamps `basis_at_authoring` from it and a DB
                  // CHECK enforces the pair), so every markup override save on
                  // the live leg was refused while the mock accepted it. The
                  // read direction has mapped this enum since the anchor landed
                  // (`mapSpecialItemPrice`); only the WRITE map was missing.
                  // Omitted on the FIXED arm, which is what the contract says:
                  // a fixed price has no base to name.
                  basisAtAuthoring: PRICING_ANCHOR_TO_GQL[row.basisAtAuthoring],
                }),
            reason,
            validFrom: row.validFrom,
            validTo: row.validTo,
            isActive: row.isActive,
          },
        }
      );
      const payload = adminPayload(result, 'specialItemPriceSave');
      return liveWriteResult(
        mapSpecialItemPrice(pickById(payload.specialItemPrices, row.id, 'specialItemPrices'))
      );
    }
    return write<SpecialItemPrice, SpecialItemPrice>('put', '/pricing/special-prices', {
      data: row,
      reason,
      actorName,
    });
  },

  setSpecialPriceActive: async (
    id: string,
    isActive: boolean,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<SpecialItemPrice>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.specialItemPriceSetActive,
        { input: { id, isActive, reason } }
      );
      const payload = adminPayload(result, 'specialItemPriceSetActive');
      return liveWriteResult(
        mapSpecialItemPrice(pickById(payload.specialItemPrices, id, 'specialItemPrices'))
      );
    }
    return write<{ isActive: boolean }, SpecialItemPrice>(
      'patch',
      `/pricing/special-prices/${id}/active`,
      { data: { isActive }, reason, actorName }
    );
  },

  saveCustomerPricing: async (
    row: CustomerPricing,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<CustomerPricing>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.customerPriceAssignmentSave,
        {
          input: {
            customerId: row.customerId,
            priceLevelId: row.priceLevelId,
            ...(row.discountPct !== undefined ? { discountPct: row.discountPct } : {}),
            reason,
          },
        }
      );
      const payload = adminPayload(result, 'customerPriceAssignmentSave');
      const saved = (payload.customerPriceAssignments ?? []).find(
        (candidate) => candidate.customerId === row.customerId
      );
      if (!saved) {
        throw new Error(`pricing.api: customerPriceAssignmentSave did not echo ${row.customerId}`);
      }
      return liveWriteResult(mapCustomerPricing(saved));
    }
    return write<CustomerPricing, CustomerPricing>('put', '/pricing/customer-pricing', {
      data: row,
      reason,
      actorName,
    });
  },

  /** Cost-basis edit. There is no `purchaseCost` member — read-only (S9.1). */
  patchCostRecord: async (
    recordId: string,
    patch: {
      landedAllocation?: number;
      internalMarkupPct?: number;
      salesMarkupPct?: number;
      basis?: CostRecord['basis'];
      effectiveDate?: string;
      trackLandedCost?: boolean;
      landedCostAllocationBasis?: CostRecord['landedCostAllocationBasis'];
    },
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<CostRecord>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.costRecordPatch,
        {
          input: {
            recordId,
            reason,
            ...(patch.landedAllocation !== undefined
              ? { landedAllocation: String(patch.landedAllocation) }
              : {}),
            ...(patch.internalMarkupPct !== undefined
              ? { internalMarkupPct: patch.internalMarkupPct }
              : {}),
            ...(patch.salesMarkupPct !== undefined
              ? { salesMarkupPct: patch.salesMarkupPct }
              : {}),
            ...(patch.basis !== undefined ? { basis: COST_BASIS_TO_GQL[patch.basis] } : {}),
            ...(patch.effectiveDate !== undefined ? { effectiveDate: patch.effectiveDate } : {}),
            ...(patch.trackLandedCost !== undefined
              ? { trackLandedCost: patch.trackLandedCost }
              : {}),
            ...(patch.landedCostAllocationBasis !== undefined &&
            patch.landedCostAllocationBasis !== null
              ? {
                  landedCostAllocationBasis:
                    ALLOCATION_BASIS_TO_GQL[patch.landedCostAllocationBasis],
                }
              : {}),
          },
        }
      );
      const payload = adminPayload(result, 'costRecordPatch');
      return liveWriteResult(mapCostRecord(pickById(payload.costRecords, recordId, 'costRecords')));
    }
    return write<typeof patch, CostRecord>('patch', `/pricing/cost-records/${recordId}`, {
      data: patch,
      reason,
      actorName,
    });
  },

  saveQuantitySchedule: async (
    schedule: QuantityPricingSchedule,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<QuantityPricingSchedule>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.quantityPricingScheduleSave,
        {
          input: {
            id: schedule.id,
            code: schedule.code,
            nameEn: schedule.nameEn,
            nameZh: schedule.nameZh,
            basis: QUANTITY_BASIS_TO_GQL[schedule.basis],
            brackets: schedule.brackets.map((bracket) => ({
              minQuantity: bracket.minQuantity,
              discountPct: bracket.discountPct,
            })),
            isActive: schedule.isActive,
            reason,
          },
        }
      );
      const payload = adminPayload(result, 'quantityPricingScheduleSave');
      return liveWriteResult(
        mapQuantitySchedule(
          pickById(payload.quantityPricingSchedules, schedule.id, 'quantityPricingSchedules')
        )
      );
    }
    return write<QuantityPricingSchedule, QuantityPricingSchedule>(
      'put',
      '/pricing/quantity-schedules',
      { data: schedule, reason, actorName }
    );
  },

  savePricingGroup: async (group: PricingGroup): Promise<PricingWriteResult<PricingGroup>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.pricingGroupSave,
        {
          input: {
            id: group.id,
            code: group.code,
            nameEn: group.nameEn,
            nameZh: group.nameZh,
            // The two fields the groups quick-create collects beside the three
            // identity ones — the taxonomy link the operator picked and the
            // group's standard rate. Both were dropped by this leg, which is
            // the SAME silent write loss `quickCreatePayload.ts` was extracted
            // to stop one layer up: declared in the form, stripped in transit,
            // optional in the type, save toasts success.
            // `?? null` rather than a conditional spread, because a save
            // REPLACES the group: absent has to CLEAR a stored value, not
            // preserve it. `0` survives — it is a deliberate 0%, not an empty.
            categoryId: group.categoryId ?? null,
            defaultDiscountPct: group.defaultDiscountPct ?? null,
            isActive: group.isActive,
          },
        }
      );
      const payload = adminPayload(result, 'pricingGroupSave');
      return liveWriteResult(
        mapPricingGroup(pickById(payload.pricingGroups, group.id, 'pricingGroups'))
      );
    }
    return write<PricingGroup, PricingGroup>('put', '/pricing/groups', { data: group });
  },

  saveGroupDiscount: async (
    card: CustomerGroupDiscount,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<CustomerGroupDiscount>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.customerGroupDiscountSave,
        {
          input: {
            id: card.id,
            customerId: card.customerId,
            groupId: card.groupId,
            kind: GROUP_DISCOUNT_KIND_TO_GQL[card.kind],
            ...(card.kind === 'discount_pct'
              ? { discountPct: card.discountPct }
              : { priceLevelId: card.priceLevelId }),
            reason,
          },
        }
      );
      const payload = adminPayload(result, 'customerGroupDiscountSave');
      return liveWriteResult(
        mapGroupDiscount(pickById(payload.customerGroupDiscounts, card.id, 'customerGroupDiscounts'))
      );
    }
    return write<CustomerGroupDiscount, CustomerGroupDiscount>('put', '/pricing/group-discounts', {
      data: card,
      reason,
      actorName,
    });
  },

  saveCurrencyPriceRow: async (
    row: CurrencyPriceRow,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<CurrencyPriceRow>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.currencyPriceRowSave,
        {
          input: {
            id: row.id,
            productId: row.productId,
            priceLevelId: row.priceLevelId,
            currency: row.currency,
            price: String(row.price),
            fixedPrice: row.fixedPrice,
            reason,
          },
        }
      );
      const payload = adminPayload(result, 'currencyPriceRowSave');
      return liveWriteResult(
        mapCurrencyPriceRow(pickById(payload.currencyPriceRows, row.id, 'currencyPriceRows'))
      );
    }
    return write<CurrencyPriceRow, CurrencyPriceRow>('put', '/pricing/currency-prices', {
      data: row,
      reason,
      actorName,
    });
  },

  saveProductPricing: async (
    fields: ProductPricingFields,
    reason: string,
    actorName: string
  ): Promise<PricingWriteResult<ProductPricingFields>> => {
    if (isLiveModule('pricing')) {
      const result = await gqlMutate<PricingAdminResult, { input: Record<string, unknown> }>(
        pricingOperations.productPricingFieldsSave,
        {
          input: {
            productId: fields.productId,
            pricingGroupId: fields.pricingGroupId ?? null,
            quantityScheduleId: fields.quantityScheduleId ?? null,
            ...(fields.costEstimateType != null
              ? { costEstimateType: COST_ESTIMATE_TYPE_TO_GQL[fields.costEstimateType] }
              : {}),
            ...(fields.quantityBrackets != null
              ? {
                  quantityBrackets: fields.quantityBrackets.map((bracket) => ({
                    minQuantity: bracket.minQuantity,
                    discountPct: bracket.discountPct,
                  })),
                }
              : {}),
            reason,
          },
        }
      );
      const payload = adminPayload(result, 'productPricingFieldsSave');
      const saved = (payload.productPricingFields ?? []).find(
        (candidate) => candidate.productId === fields.productId
      );
      if (!saved) {
        throw new Error(
          `pricing.api: productPricingFieldsSave did not echo ${fields.productId}`
        );
      }
      return liveWriteResult(mapProductPricingFields(saved));
    }
    return write<ProductPricingFields, ProductPricingFields>('put', '/pricing/product-pricing', {
      data: fields,
      reason,
      actorName,
    });
  },
};
